import { Component, computed, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { EmploymentDialogComponent } from '../../components/employment-dialog/employment-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import { AssessmentService } from '../../services/assessment.service';
import { EmploymentDetails } from '../../data/employment-details';
import { OccupationDetails } from '../../data/occupation-details';
import { IFormComponent } from '../../models/i-form-component';
import { LookupItem } from '../../models/lookup-item';
import { map, Subject, takeUntil } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';

@Component({
  selector: 'app-occupation-details',
  templateUrl: './occupation-details.component.html',
  styleUrl: './occupation-details.component.scss'
})
export class OccupationDetailsComponent implements OnInit, IFormComponent {
  @Input() model!: InitialAssessmentForm;
  @Input() formTypes!: any;
  @Input() countries!: LookupItem[];
  @Output() formReady = new EventEmitter<FormGroup>();

  displayedColumns = ['employer','country','position','start','end','edit', 'delete'];
  roleDataSource = new MatTableDataSource<EmploymentDetails>([]);
  submitAttempted = false;
  formBuilder = inject(NonNullableFormBuilder);

  occupationForm = this.formBuilder.group({
    occupation: [''],
    workedIn: [''],
    professionalYear: ['']
  },
  {
      validators: requiredListWhenYesValidator([
            {
              controlName: 'workedIn',
              errorKey: 'workedInRequired',
              getItems: () => this.model?.occupation?.roles ?? []
            }])
    });
  
  isVevo!: boolean;
  isVevoEmployee!: boolean;
  isVisit!: boolean;
  isSkilled!: boolean;

  constructor(private dialog: MatDialog, private assessmentService: AssessmentService) { }
  
 private destroy$ = new Subject<void>();
 
      private breakpointObserver = inject(BreakpointObserver);
      isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
        map(result => result.matches),
        takeUntil(this.destroy$)
      );
    
    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }

  ngOnInit(): void {

    this.model = this.model || new InitialAssessmentForm();
    this.model.occupation = this.model.occupation || new OccupationDetails();
    this.roleDataSource.data = this.model.occupation!.roles;

    this.isVevo = this.assessmentService.isVevo();
    this.isVevoEmployee = this.assessmentService.isVevoEmployee();
    this.isVisit = this.assessmentService.isVisit();
    this.isSkilled = this.assessmentService.isSkilled();

    // Bind form to model data
    this.occupationForm.patchValue(this.model.occupation);
    // Subscribe to form changes to update model
    this.occupationForm.valueChanges.subscribe(value => {
      this.model.occupation = {
        ...this.model.occupation!,
        ...value
      }
    });

    this.formReady.emit(this.occupationForm);
  }

  refreshRoleTable(): void {
    this.roleDataSource.data = [...this.model.occupation!.roles];
  }

  add(): void {
    const dialogRef = this.dialog.open(EmploymentDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { model: null, countries: this.countries }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.occupation!.roles.push(result);
        this.refreshRoleTable();
        this.occupationForm.controls.workedIn.updateValueAndValidity();
      }
    });
  }

  remove(index: number): void {
    this.model.occupation!.roles.splice(index, 1);
    this.refreshRoleTable();
    this.occupationForm.controls.workedIn.updateValueAndValidity();
  }

  edit(index: number): void {
    const role = this.model.occupation!.roles[index];
    const dialogRef = this.dialog.open(EmploymentDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { model: role, countries: this.countries }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.occupation!.roles[index] = result;
        this.refreshRoleTable();
        this.occupationForm.controls.workedIn.updateValueAndValidity();
      }
    });
  }

  employmentHistoryLabel = computed(() =>{
    if(this.assessmentService.isWork())
      return 'Provide details of your employment history in this occupation or a closely related occupation.';
    if(this.assessmentService.isVisit())
      return 'Provide details of your current employment situation';
    // Default for not work or visit    
    return 'Provide details of your employment history for the last 10 years';
  });

  // Unicode bullet character *
  bullet = "\u2022";


  // This should probably be a dialog
  professionalHelpLabel = `
  The Professional Year (PY) is a structured professional development program combinig formal learning and workplace experience.
  Professional years are conducted by the following professional bodies:
    - Australian Computer Society (ACS)
    - CPA Australia
    - Institute of Chartered Accountants Australia
    - Institute of Public Accountants
    - Engineers Australia
  `.replace(/-/g, this.bullet);

  isFormValid(): boolean {
    this.submitAttempted = true;
    return this.occupationForm.valid;
  }
  markFormAsTouched(): void {
    this.occupationForm.markAllAsTouched();
  }
  
}



